import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {ChallengeBuilderProject, ChallengeObjective} from 'src/app/core/model/project';
import {
  ChallengeObjectivesAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {CbCheckboxOptionComponent, CheckboxOption} from '../shared/cb-checkbox-option.component';

interface LocalObjective extends ChallengeObjective {
  uid: string;
}

@Component({
  selector: 'cb-objectives',
  standalone: true,
  imports: [CbCheckboxOptionComponent],
  templateUrl: './cb-objectives.component.html',
  styleUrl: './cb-objectives.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbObjectivesComponent implements OnInit {
  private static readonly MAX_OBJECTIVES = 10;
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private uidCounter = 0;

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject>();

  onNext = output<void>();
  onBack = output<void>();

  // Internal state
  objectives = signal<LocalObjective[]>([]);

  // Track initial state for change detection on save
  private initialObjectives = signal<ChallengeObjective[]>([]);
  private hasLoadedFromProject = false;

  // Sync objectives when project changes
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly syncObjectivesEffect = effect(
    () => {
      const project = this.cbProject();
      if (!project || this.hasLoadedFromProject) return;

      const projectObjectives = project.challengeBuilder?.objectives || [];
      this.hasLoadedFromProject = true;

      this.objectives.set([
        ...projectObjectives.map(o => ({...o, uid: this.generateUid()})),
        {name: '', selected: false, uid: this.generateUid()},
      ]);
      // Store only selected objectives for comparison on save
      this.initialObjectives.set(projectObjectives.filter(o => o.selected).map(o => ({...o})));
    },
    {allowSignalWrites: true}
  );

  private generateUid(): string {
    return `obj-${++this.uidCounter}-${Date.now()}`;
  }

  // Update validation state whenever objectives change
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly validationEffect = effect(
    () => {
      const hasSelectedObjective = this.objectives().some(o => o.selected && o.name.trim().length > 0);
      const hasDuplicates = this.duplicateInputIndices().size > 0;
      const isValid = hasSelectedObjective && !hasDuplicates;

      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: isValid}));
    },
    {allowSignalWrites: true}
  );

  // Computed: which objective indices have duplicate names
  duplicateInputIndices = computed(() => {
    const objectives = this.objectives();
    const duplicates = new Set<number>();

    for (let i = 0; i < objectives.length; i++) {
      const val = objectives[i].name.trim().toLowerCase();
      if (!val) continue;

      for (let j = 0; j < objectives.length; j++) {
        if (i === j) continue;
        if (objectives[j].name.trim().toLowerCase() === val) {
          duplicates.add(i);
          break;
        }
      }
    }

    return duplicates;
  });

  goal = computed(() => {
    return this.cbProject()?.challengeBuilder?.goals?.find(g => g.selected)?.name || '';
  });

  hasMultipleObjectives = computed(() => {
    return this.objectives().filter(o => o.name.trim().length > 0).length > 1;
  });

  options = computed((): CheckboxOption[] => {
    return this.objectives().map((objective, index) => ({
      text: 'Add objective',
      isChecked: objective.selected,
      isDisabled: false,
      isInput: true,
      inputIndex: index,
      uid: objective.uid,
    }));
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.nextClickSubscription = this.nextClick$().subscribe(() => {
      this.onNextClick();
    });
    this.backClickSubscription = this.backClick$().subscribe(() => {
      this.onBackClick();
    });
  }

  onOptionToggle(option: CheckboxOption, checked: boolean): void {
    this.objectives.update(objectives => {
      const updated = objectives.map((o, i) => (i === option.inputIndex ? {...o, selected: checked} : o));
      return this.normalizeObjectives(updated);
    });
  }

  onRemove(index: number): void {
    this.objectives.update(objectives => {
      const updated = objectives.filter((_, i) => i !== index);
      return this.normalizeObjectives(updated);
    });
  }

  onInputChange(value: string, index: number): void {
    this.objectives.update(objectives => {
      const updated = objectives.map((o, i) =>
        i === index ? {...o, name: value, selected: value.trim().length > 0} : o
      );
      return this.normalizeObjectives(updated);
    });
  }

  private normalizeObjectives(objectives: LocalObjective[]): LocalObjective[] {
    const meaningful = objectives.filter(o => o.name.trim().length > 0 || o.selected);
    if (meaningful.length >= CbObjectivesComponent.MAX_OBJECTIVES) {
      return meaningful;
    }
    const existingEmpty = objectives.find(o => !o.name.trim() && !o.selected);
    return [...meaningful, existingEmpty || {name: '', selected: false, uid: this.generateUid()}];
  }

  private onNextClick(): void {
    const allObjectives = this.objectives()
      .filter(o => o.name.trim().length > 0)
      .map(o => ({...o, name: o.name.trim()}));

    const objectivesAltered = this.getAlteredObjectives(allObjectives);

    this.saveObjectives(allObjectives, objectivesAltered, () => this.onNext.emit());
  }

  private onBackClick(): void {
    const allObjectives = this.objectives()
      .filter(o => o.name.trim().length > 0)
      .map(o => ({...o, name: o.name.trim()}));

    this.saveObjectives(allObjectives, [], () => this.onBack.emit());
    this.store$.dispatch(new SetBackLoadingAction({backLoading: true}));
  }

  private saveObjectives(
    allObjectives: ChallengeObjective[],
    objectivesAltered: ChallengeObjective[],
    onSuccess?: () => void
  ): void {
    this.store$.dispatch(
      new ChallengeObjectivesAction({
        projectId: this.cbProject()?._id || '',
        objectives: allObjectives,
        objectivesAltered,
        onSuccess: () => {
          if (this.destroyed) return;
          onSuccess?.();
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save objectives', 'error');
          console.error('Failed to save objectives:', error);
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
      })
    );
  }

  private getAlteredObjectives(allObjectives: ChallengeObjective[]): ChallengeObjective[] {
    const initialSelected = this.initialObjectives();
    const finalSelected = allObjectives.filter(o => o.selected);

    return finalSelected.filter(objective => {
      const match = initialSelected.find(init =>
        init._id ? init._id === objective._id : init.name === objective.name
      );

      // New objective not in initial selected set
      if (!match) return true;

      // Existing objective that has been altered (e.g. tasks cleared)
      return objective.name !== match.name || !objective.tasks?.length;
    });
  }
}
